'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import {
  BlogTLDR,
  BlogKeyTakeaways,
  BlogFAQ,
  BlogSources,
  BlogLastUpdated,
  BlogComparisonTable,
  BlogAppCTA,
  BlogWhatTheAppChecks,
} from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Does Graza Olive Oil Have Microplastics? The Squeeze Bottle Question"
      author="MicroPlastics Research Desk"
      date="July 2, 2026"
      readTime="10 min read"
      heroImage="/blog/heroes/food.webp"
      heroImageAlt="Olive oil in a squeeze bottle, a dark glass bottle and a tin on a counter"
    >
      <BlogLastUpdated iso="2026-08-17" formatted="August 17, 2026" />

      <BlogTLDR>
        <strong>Updated August 2026: an independent test now exists, and it
        found something the squeeze bottle cannot explain.</strong>{' '}
        Oasis, a product-testing app, ran Graza extra virgin olive oil by{' '}
        <strong>Agilent 8700 LDIR spectroscopy</strong> and reported{' '}
        <strong>2 microplastic particles per millilitre — one PTFE, one
        rubber</strong>, roughly 30 per tablespoon. The striking part is the
        polymer: <strong>PTFE is not what the squeeze bottle is made
        of</strong>, so on that result the bottle is not the source — which
        points upstream at processing equipment, where PTFE is standard gasket
        and hose-lining material. Graza itself publishes no microplastic
        testing, and neither does any other olive oil brand, so this is one
        independent result rather than an industry picture. The format argument
        below still stands, and if anything it stands harder. Olive oil is
        close to <strong>100% fat</strong>, and fat
        is the most effective medium there is for pulling additives out of a
        polymer. Food-contact regulators formalise this, treating fatty foods as a
        more aggressive test condition than watery ones. Graza&apos;s signature
        format is a <strong>plastic squeeze bottle</strong>, which puts a high-fat
        food in sustained contact with a polymer for the weeks or months it takes
        to use up, and squeezing adds mechanical stress on top. That makes olive
        oil in plastic a <strong>more demanding case than water in plastic</strong>,
        not a lesser one. Graza also sells <strong>refill tins</strong>, and the
        clean answer for any brand is <strong>dark glass or tin</strong>.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>An independent LDIR test reported 2 particles/mL in Graza EVOO — one PTFE, one rubber</strong> (Oasis, 2026). No olive oil <em>brand</em> publishes its own testing, so treat this as one third-party result, not an industry benchmark.</>,
          <><strong>PTFE is not the bottle material</strong>, and it is a PFAS. On that finding the packaging is not the source — processing equipment is, where PTFE is standard for gaskets, seals and hose linings. See <Link href="/blog/pfas-in-cooking-oil" className="text-forest underline">PFAS in cooking oil</Link>.</>,
          <>Separately, Graza <strong>tested positive for 5 plasticisers across two 2025 tests</strong>, including the phthalate DEHP at roughly 130 ppb. Phthalates are the better-documented contaminant in this category.</>,
          <>Fat is the aggressive medium. Many polymer additives are lipophilic, so they migrate into oil far more readily than into water.</>,
          <>Squeezing is mechanical stress, an established contributor to particle release, applied every single time you use the bottle.</>,
          <>Clear plastic adds a second, separate problem: light drives oxidation and degrades the oil&apos;s quality and polyphenols.</>,
          <>Graza sells refill tins. Buying the tin and decanting into glass gets you the brand without the format.</>,
        ]}
      />

      <RiskQuizInlineNudge source="blog_inline_graza" />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why oil is a harder case than water
      </h2>
      <p>
        Most microplastics coverage is about water, which has quietly trained
        people to think of plastic packaging as a single, uniform risk. It is not.
        Migration out of a polymer depends heavily on{' '}
        <strong>how well the migrating substance dissolves in the food</strong>,
        and a great many plasticisers, stabilisers and processing aids are{' '}
        <strong>lipophilic</strong> — they dissolve readily in fat and poorly in
        water.
      </p>
      <p>
        This is not an inference. It is written into how food-contact materials are
        assessed: regulators specify{' '}
        <strong>fatty-food simulants</strong> as a distinct and more aggressive test
        category than aqueous or acidic simulants, precisely because fat extracts
        more. Olive oil is itself one of the standard fatty simulants used in that
        testing.
      </p>
      <p>
        So a litre of olive oil sitting in a plastic bottle is, chemically, a more
        effective extraction system than a litre of water in the same bottle. It is
        the same argument that makes{' '}
        <Link href="/blog/does-cheese-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          cheese
        </Link>{' '}
        a more demanding case than milk, and oil-packed{' '}
        <Link href="/blog/does-canned-tuna-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          canned tuna
        </Link>{' '}
        a more demanding case than water-packed.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The three things a squeeze bottle adds
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Sustained fat contact.</strong> A bottle of olive oil is not
          consumed in one sitting. It sits in a warm kitchen, next to a hob, for
          weeks or months, with fat against polymer the entire time. Duration
          matters as much as any other variable.
        </li>
        <li>
          <strong>Mechanical stress, repeatedly.</strong> Squeezing deforms the
          bottle wall. Mechanical stress is a documented contributor to particle
          release from plastic containers, and a squeeze bottle is the one format
          where you deliberately flex the container every time you use it. A
          rigid bottle you pour from does not do this.
        </li>
        <li>
          <strong>Light, if the plastic is clear or translucent.</strong> This is a
          separate issue from plastics and it matters for the product you are
          buying. Light accelerates oxidation of olive oil, degrading flavour and
          the polyphenols that are much of the reason to buy good oil. It is why
          serious producers use dark glass or opaque tins. A translucent bottle on
          a sunlit counter is working against the oil.
        </li>
      </ul>
      <p>
        Worth saying plainly: <strong>the squeeze bottle is genuinely good
        design</strong> for controlling how you dress a salad or finish a dish. It
        exists because pouring from a heavy tin is awkward. The trade-off is real
        in both directions, and the fix does not require giving up the format
        entirely, as below.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Formats compared
      </h2>

      <BlogComparisonTable
        caption="Olive oil packaging through a plastics lens. Ranked on fat contact, mechanical stress and light exposure, since brand-published particle counts still do not exist."
        columns={[
          { key: 'format', label: 'Format' },
          { key: 'plastic', label: 'Fat-to-plastic contact' },
          { key: 'light', label: 'Light protection' },
          { key: 'verdict', label: 'Verdict' },
        ]}
        rows={[
          { format: 'Dark glass bottle', plastic: 'None (cap liner only)', light: 'Good', verdict: 'Best overall on both counts' },
          { format: 'Metal tin', plastic: 'Thin internal liner', light: 'Excellent, fully opaque', verdict: 'Excellent. Best for bulk and storage' },
          { format: 'Clear glass bottle', plastic: 'None (cap liner only)', light: 'Poor', verdict: 'Clean on plastic, exposed to light. Store dark' },
          { format: 'Tin refill + decant to glass', plastic: 'Minimal', light: 'Excellent', verdict: 'Best practical setup' },
          { format: 'Opaque plastic squeeze bottle', plastic: 'Sustained, plus flexing', light: 'Good', verdict: 'Moderate. Light handled, polymer contact not' },
          { format: 'Clear/translucent plastic squeeze bottle', plastic: 'Sustained, plus flexing', light: 'Poor', verdict: 'Weakest. Both problems at once' },
          { format: 'Large plastic jug (catering)', plastic: 'Sustained, long shelf life', light: 'Varies', verdict: 'Weak. Long contact, slow use' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        If you like Graza, what to actually do
      </h2>
      <p>
        This does not have to be a boycott, and there is a straightforward path
        that keeps what is good about the product:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Buy the refill tin.</strong> Graza sells larger tins intended to
          refill the squeeze bottle. A tin is one of the best formats available:
          opaque and with only a thin internal liner rather than a polymer wall.
        </li>
        <li>
          <strong>Decant into a dark glass bottle</strong> with a pour spout, or a
          glass cruet. You keep controlled pouring and lose the plastic entirely.
          If you want the squeeze action specifically, decant smaller amounts and
          use it up faster so nothing sits in plastic for months.
        </li>
        <li>
          <strong>Keep it away from the hob and out of the sun.</strong> Heat and
          light both work against you, whatever the container. A cupboard is better
          than a windowsill.
        </li>
        <li>
          <strong>Buy quantities you will finish.</strong> Contact time is a
          variable you fully control. A bottle used in six weeks has had far less
          exposure than one nursed for eight months.
        </li>
        <li>
          <strong>Never store or heat oil in plastic.</strong> Warm fat against
          polymer is the worst combination of the three factors that matter.
        </li>
      </ul>
      <p>
        For how the wider category compares, see{' '}
        <Link href="/blog/olive-oil-brands-ranked-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          olive oil brands ranked by packaging
        </Link>
        .
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>The container material and light exposure of the exact oil you buy, including whether a tin refill exists for it.</>,
          <>A 0&ndash;100 risk score that weights fat content, contact duration and format together rather than treating all bottles alike.</>,
          <>A lower-plastic swap in the same price and quality bracket, usually a tin or dark glass version.</>,
          <>Your running pantry profile, so a cooking staple you use daily is scored accordingly.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan the oil on your counter"
        sub="Olive oil is the fattiest thing in most kitchens, which makes the container matter more than it does for water. Scan yours with MicroPlastics for a 0&ndash;100 score and a cleaner format of the same oil."
        buttonLabel="Scan my olive oil"
        source="graza_inline_scan"
        postSlug="does-graza-have-microplastics"
        queryFamily="graza_microplastics"
      />

      <p>
        Related:{' '}
        <Link href="/blog/olive-oil-brands-ranked-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          olive oil brands ranked by packaging
        </Link>
        ,{' '}
        <Link href="/blog/does-cheese-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          does cheese have microplastics
        </Link>
        ,{' '}
        <Link href="/blog/does-canned-tuna-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          does canned tuna have microplastics
        </Link>
        , and{' '}
        <Link href="/blog/best-plastic-free-food-storage" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          best plastic-free food storage
        </Link>
        .
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Does Graza olive oil have microplastics?',
            answer:
              'One independent test now exists. Oasis, a product-testing app, analysed Graza extra virgin olive oil by Agilent 8700 LDIR spectroscopy and reported 2 microplastic particles per millilitre — one PTFE and one rubber — which works out at roughly 30 per tablespoon. The significant detail is that PTFE is not the material the squeeze bottle is made from, so on that result the packaging is not the source, and processing equipment is the more likely origin since PTFE is standard for gaskets, seals and hose linings in food production. Graza itself publishes no microplastic testing, and neither does any other olive oil brand, so this is a single third-party result rather than an industry benchmark. Separately, Graza tested positive for five plasticisers across two 2025 tests, including the phthalate DEHP at around 130 ppb. The format point still holds regardless: olive oil is almost entirely fat, many polymer additives are fat-soluble, and a plastic squeeze bottle combines sustained fat contact with repeated flexing. Graza also sells refill tins, which are a much better format.',
          },
          {
            question: 'Is olive oil in plastic worse than water in plastic?',
            answer:
              'For migration from the container, generally yes. Food-contact regulations specify fatty-food simulants as a more aggressive test category than aqueous ones because fat extracts more from polymers, and olive oil is itself one of the standard fatty simulants used in that testing. Oil also sits in its container for weeks or months, whereas a bottle of water is usually drunk quickly.',
          },
          {
            question: 'Does squeezing the bottle make it worse?',
            answer:
              'Plausibly, yes. Mechanical stress is an established contributor to particle release from plastic containers, and a squeeze bottle is the one format where you deliberately deform the container wall every single time you use it. A rigid bottle that you pour from does not have that. It is one reason a tin or glass bottle with a pour spout is a better setup.',
          },
          {
            question: 'Should olive oil be in dark glass or a tin?',
            answer:
              'Either is good, and for slightly different reasons. Dark glass has no polymer in contact with the oil at all beyond the cap liner, and blocks most light. A tin is fully opaque, so it is the best light protection available, and has only a thin internal liner rather than a polymer wall. The best practical setup is buying a tin and decanting working amounts into a dark glass bottle.',
          },
          {
            question: 'Does light really matter for olive oil?',
            answer:
              'Yes, and it is a separate issue from plastics that affects what you are paying for. Light accelerates oxidation, which degrades flavour and reduces the polyphenol content that is much of the point of buying good extra virgin oil. This is why quality producers use dark glass or opaque tins, and why a clear or translucent bottle on a sunny counter is the worst place to keep it.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Oasis',
            year: 2026,
            title: 'Graza Drizzle extra virgin olive oil — microplastics lab report: 2 particles/mL (one PTFE, one rubber), ~30 per tablespoon, by Agilent 8700 LDIR spectroscopy. Independent third-party test, not peer-reviewed; the brand publishes no testing of its own',
            publisher: 'Oasis Health',
            url: 'https://www.oasishealth.app/labs/1884',
          },
          {
            authors: 'European Commission',
            year: 2011,
            title: 'Regulation (EU) No 10/2011 on plastic materials intended to come into contact with food (food simulants)',
            publisher: 'Official Journal of the European Union',
            url: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A32011R0010',
          },
          {
            authors: 'Winkler A, Santo N, Ortenzi MA, et al.',
            year: 2022,
            title: 'Does mechanical stress cause microplastic release from plastic water bottles?',
            publisher: 'Water Research',
            url: 'https://www.sciencedirect.com/science/article/pii/S0043135422000453',
          },
          {
            authors: 'Kedzierski M, Lechat B, Sire O, et al.',
            year: 2020,
            title: 'Microplastic contamination of packaged meat: occurrence and associated risks',
            publisher: 'Food Packaging and Shelf Life',
            url: 'https://www.sciencedirect.com/science/article/abs/pii/S2214289420301137',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
